import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { TradingSymbolView } from "../../../../shared/_types/trading-symbol.type";
import { CommandResult } from "../../../../shared/_types/command-result.type";

@Injectable({
    providedIn: "root",
})
export class TradingSymbolsService {
    constructor(private http: HttpClient) {}

    list(request: ListTradingSymbolsRequest): Observable<ListTradingSymbolsResponse> {
        return this.http.post<ListTradingSymbolsResponse>("/trading-symbols/list", request);
    }

    get(id: string): Observable<TradingSymbol> {
        return this.http.get<TradingSymbol>(`/trading-symbols/${id}`);
    }

    activate(id: string): Observable<CommandResult> {
        return this.http.patch<CommandResult>(`/trading-symbols/${id}/activate`, null);
    }

    deactivate(id: string): Observable<CommandResult> {
        return this.http.patch<CommandResult>(`/trading-symbols/${id}/deactivate`, null);
    }

    update(id: string, symbol: TradingSymbol): Observable<CommandResult> {
        return this.http.patch<CommandResult>(`/trading-symbols/${id}`, symbol);
    }

    create(symbol: TradingSymbol): Observable<CommandResult> {
        return this.http.post<CommandResult>("/trading-symbols", symbol);
    }

    delete(id: string): Observable<CommandResult> {
        return this.http.delete<CommandResult>(`/trading-symbols/${id}`);
    }

    getMarketInfo(symbol: string): Observable<SymbolMarketInfo> {
        return this.http.get<SymbolMarketInfo>(`/trading-symbols/${symbol}/market-info`);
    }

    getTradingSymbolGroups(): Observable<TradingSymbolGroup[]> {
        return this.http.get<TradingSymbolGroup[]>("/trading-symbol-groups");
    }

    getExchanges(): Observable<Exchange[]> {
        return this.http.get<Exchange[]>("/exchanges");
    }
}

export type TradingSymbolGroup = {
    id: string;
    name: string;
};

export type Exchange = {
    id: string;
    name: string;
    code: string;
    icon?: string | null | undefined;
};

export type SymbolMarketInfo = {
    isSuccess: boolean;
    error?: string | null | undefined;
    result?:
        | {
              symbol: string;
              companyName: string;
              exchangeId: string;
              type: TradingSymbolType;
              icon?: string | null | undefined;
              description?: string | null | undefined;
          }
        | null
        | undefined;
};

export type ListTradingSymbolsRequest = {
    page: number;
    itemsPerPage: number;
    searchText?: string | null | undefined;
};

export type ListTradingSymbolsResponse = {
    total: number;
    symbols: TradingSymbolView[];
};

export type TradingSymbol = {
    id: string;
    symbol: string;
    companyName: string;
    icon?: string | null;
    type: TradingSymbolType;
    tradingGroupId: string;
    exchangeId: string;
    isActive: boolean;
    description?: string | null;
    comment?: string | null;
};

export enum TradingSymbolType {
    Undefined = 0,
    CS = 1, //Common Stock
    PFD = 2, //Preferred Stock
    WARRANT = 3, //Warrant
    RIGHT = 4, //Rights
    BOND = 5, //Corporate Bond
    ETF = 6, //Exchange Traded Fund
    ETN = 7, //Exchange Traded Note
    ETV = 8, //Exchange Traded Vehicle
    SP = 9, //Structured Product
    ADRC = 10, //American Depository Receipt Common,
    ADRP = 11, //American Depository Receipt Preferred
    ADRW = 12, //American Depository Receipt Warrants
    ADRR = 13, //American Depository Receipt Rights
    FUND = 14, //Fund
    BASKET = 15, //Basket
    UNIT = 16, //Unit
    LT = 17, //Liquidating Trust
    OS = 18, //Ordinary Shares
    GDR = 19, //Global Depository Receipts
    OTHER = 20, //Other Security Type
    NYRS = 21, //New York Registry Shares
    AGEN = 22, //Agency Bond
    EQLK = 23, //Equity Linked Bond
    ETS = 24, //Single-security ETF
    INDEX = 25, // Stock Market Index — not tradable
}
